export type Point = {
	x: number;
	y: number;
};

export type Size = {
	width: number;
	height: number;
};

export type Rect = {
	x: number;
	y: number;
	width: number;
	height: number;
};

export type Line = {
	start: Point;
	end: Point;
};

// returns true if the parent contains the child
// in the case where the parent and child overlap exactly, true is returned
// false is returned only when the child lies outside of the parent
export const rectContainsRect = (parent: Rect, child: Rect): boolean => {
	return (
		between(child.x, parent.x, parent.x + parent.width) &&
		between(child.x + child.width, parent.x, parent.x + parent.width) &&
		between(child.y, parent.y, parent.y + parent.height) &&
		between(child.y + child.height, parent.y, parent.y + parent.height)
	);
};

export const rectsOverlap = (rect1: Rect, rect2: Rect): boolean => {
	// Two rectangles overlap if:
	// 1. One rectangle is NOT completely to the left of the other
	// 2. One rectangle is NOT completely above the other

	// Check if one rectangle is completely to the left of the other
	const isLeft = rect1.x + rect1.width <= rect2.x || rect2.x + rect2.width <= rect1.x;

	// Check if one rectangle is completely above the other
	const isAbove = rect1.y + rect1.height <= rect2.y || rect2.y + rect2.height <= rect1.y;

	// If either condition is true, the rectangles don't overlap
	return !(isLeft || isAbove);
};

// is x between a and b? (boundaries included)
const between = (x: number, a: number, b: number): boolean => {
	return x >= a && x <= b;
};

export const distanceBetweenPoints = (a: Point, b: Point): number => {
	return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));
};

export const getBoundingBox = (rects: Rect[]): Rect => {
	if (rects.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
	const minX = Math.min(...rects.map((r) => r.x));
	const minY = Math.min(...rects.map((r) => r.y));
	const maxX = Math.max(...rects.map((r) => r.x + r.width));
	const maxY = Math.max(...rects.map((r) => r.y + r.height));
	return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

// Given two rectangles, return a line that goes from one to the other
// This function tries to exercise good judgment
// Imageine two boxes on the canvas
// What is a natural connecting line between them?
export const getConnectingLine = (from: Rect, to: Rect): Line => {
	// I tried a fancy algorithm (see commit 9d7e57b17c7784dc6a3a18d77c42e087e358e68d)
	// but it wasn't much better than this simple approach.
	// One of the downsides of a fancy algorithm is that, since it tries to optimize
	// line placement, the lines change as you move frames around which feels disorienting.
	// So keepint it simple for now - ss
	// so i went with a simpler approach
	// connect the centers of the two rectangles
	const fromCenter = {
		x: from.x + from.width / 2,
		y: from.y + from.height / 2,
	};
	const toCenter = {
		x: to.x + to.width / 2,
		y: to.y + to.height / 2,
	};
	return {
		start: fromCenter,
		end: toCenter,
	};
};

// Returns the inner rect cropped to the bounds of the outer.
export const cropRect = (inner: Rect, outer: Rect): Rect => {
	const x = Math.max(inner.x, outer.x);
	const y = Math.max(inner.y, outer.y);
	const width = Math.min(inner.x + inner.width, outer.x + outer.width) - x;
	const height = Math.min(inner.y + inner.height, outer.y + outer.height) - y;
	return { x, y, width, height };
};
